import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Grid2,
  Box,
  Typography,
  TextField,
  Button,
  Modal,
  InputLabel,
  Tabs,
  Tab,
  Paper,
  Select,
  MenuItem,
  FormControl,
  Snackbar,
  Alert
} from "@mui/material";
import { addAssetFile, updateAsset } from "../../actions/mediaAction";
import { useForm } from "react-hook-form";
import ModalBox from "../../themes/overrides/ModalBox";
import OutlinedInput from "../../themes/overrides/OutlinedInput";
import LoadingOverlay from "../../components/LoadingOverlay";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ComputerIcon from '@mui/icons-material/Computer';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import GoogleDriveIcon from '@mui/icons-material/Storage';
import DropboxIcon from '@mui/icons-material/Folder';
import StyledTextArea from "../../themes/overrides/TextArea";
import { useParams } from 'react-router-dom';
import ai_icon from '../../assets/images/ai_icon.png'
import ImageGenerationPanel from "./ImageGenerationPanel";
import { HandleImageGenerate } from "../../components/HandleImageGenerate";
import GeneratedImagePreview from "./GeneratedImagePreview";

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index} style={{ marginTop: '20px' }}>
    {value === index && children}
  </div>
);

const AddMedia = ({ isOpen, onClose, onSubmitSuccess, editMode, asset }) => {
  const dispatch = useDispatch();
  const { workspaceId } = useParams();
  const [loading, setLoading] = useState(false);
  const submitError = useSelector((state) => state.assets.errMsg)

  const [selectedTab, setSelectedTab] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [generativeModels, setGenerativeModels] = useState([]);
  const [selectedGenerativeModel, setSelectedGenerativeModel] = useState();
  const [AIPrompt, setAIPrompt] = useState('');
  const [selectedStyle, setSelectedStyle] = useState();
  const [aiLoading, setAiLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [severity, setSeverity] = useState("success");
  const [isGenerated, setIsGenerated] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const currentWorkspace = useSelector((state) => state.workspace.workspaceInfo);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    unregister,
    formState: { errors },
  } = useForm({
    defaultValues: {
      title: "",
      description: "",
      file: null,
    },
  });

  const [error, setError] = useState("");

  useEffect(() => {
    if (editMode && asset) {
      setValue("title", asset.title);
      setValue("file", asset.fileName);
      setValue("description", asset.description);
    } else {
      reset();
    }
  }, [editMode, asset]);

  useEffect(() => {
    if (currentWorkspace?.data?.aiModels) {
      const imageModels = currentWorkspace?.data?.aiModels?.filter(model => model.type === 'image');
      if (imageModels) {
        setGenerativeModels(imageModels)
        setSelectedGenerativeModel(imageModels[0])
      }
    }
  }, [currentWorkspace]);


  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = () => {
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const files = e.dataTransfer.files;
    handleFileSelect(files);
  };

  const handleFileSelect = (files, isGenerated) => {
    if (files?.[0]) {
      setSelectedFile(files[0]);
      setIsGenerated(isGenerated);
      setValue('file', files);

      if (files[0].type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result);
        };
        reader.readAsDataURL(files[0]);
      } else {
        setPreview(null);
      }
      unregister('file');
    }
  };


  const handleInputChange = (e) => {
    const file = e.target.files[0];
    const isGenerated = e.generated || false;

    const acceptedTypes = ["image/jpg", "image/jpeg", "image/png", "image/webp", "image/svg+xml"];

    if (file && !acceptedTypes.includes(file.type)) {
      setError("Invalid file type. Only .jpg, .png, svg and .webp are allowed.");
      e.target.value = "";
    } else {
      setError("");
      handleFileSelect(e.target.files, isGenerated);
    }
  };

  const onSubmit = async (data) => {
    if (!data.title) {
      setError("Title is required.");
      return;
    }

    if (!editMode && !selectedFile) {
      setError("File is required.");
      return;
    }

    if (data.title.trim() == "") {
      setError("Title cannot be empty or just spaces");
      return;
    }

    const formData = new FormData();
    formData.append("title", data.title);
    formData.append("description", data.description);
    if (selectedFile) {
      formData.append("file", selectedFile);
    }
    formData.append("workspaceId", workspaceId);

    setLoading(true);
    try {
      if (editMode) {
        const editFormData = new FormData();
        editFormData.append("title", data.title);
        editFormData.append("description", data.description);

        const success = await dispatch(updateAsset(asset._id, editFormData));

        if (submitError) {
          setError(submitError)
        }

        if (success) {
          onSubmitSuccess();
          reset();
          onClose();
        }
      } else {
        const success = await dispatch(addAssetFile(formData));

        if (submitError) {
          setError(submitError)
        }
        if (success?.message === 'Filename already exist') {
          setError('File already exists');
          return;
        }

        if (success) {
          onSubmitSuccess(success);
          setSelectedFile(null);
          setPreview(null);
          setErrorMessage("");
          setError('');
          reset();
          onClose();
        }
      }
    } catch (error) {
      setError("Upload Failed, Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateContent = async () => {
    const result = await HandleImageGenerate({
      AIPrompt,
      selectedGenerativeModel,
      selectedStyle,
      handleInputChange,
      setAiLoading,
      setErrorMessage,
      setOpenSnackbar,
      setSeverity,
    })

    if (result) {
      setPreviewImage(result.previewURL);
      setPendingFile(result.file);
      setIsModalOpen(true);
    }
  }

  const handleCloseOrSuccessGeneratedPreview = () => {
    setPreviewImage(null);
    setPendingFile(null);
    setIsModalOpen(false);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
    setErrorMessage("");
  };

  const handleClose = () => {
    reset();
    setSelectedFile(null);
    setPreview(null);
    setAIPrompt('');
    setSelectedStyle(null);
    setSelectedTab(0);
    setError(null);
    setIsGenerated(false);
    setLoading(false);

    onClose();
  };


  return (
    <>
      <Modal open={isOpen} onClose={handleClose}>
        <ModalBox
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            width: { xs: '95%', sm: '600px', md: '700px' },
            maxHeight: { xs: '95vh', sm: '90vh' },
            position: 'relative',
          }}
        >
          <Typography variant="h2" sx={{ marginBottom: 2 }}>
            {editMode ? "Edit Media" : "Add Media"}
          </Typography>
          <Typography sx={{ fontSize: '16px', fontWeight: 400 }}>
            Title
          </Typography >
          <OutlinedInput
            fullWidth
            name="title"
            {...register("title", { required: "Title is required" })}
            error={!!errors.title}
            helperText={errors.title?.message}
          />
          <Typography sx={{ fontSize: '16px', fontWeight: 400 }}>
            Description
          </Typography>
          <OutlinedInput
            fullWidth
            name="description"
            {...register("description")}
            maxRows={4}
          />
          {editMode ? (
            <Typography>{asset.fileName}</Typography>
          ) : (
            <>
              <Box>
                <Typography sx={{ mt: 3, mb: 2 }}>
                  Select input source
                </Typography>

                <Tabs
                  value={selectedTab}
                  onChange={(e, newValue) => setSelectedTab(newValue)}
                  sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, width: '100%' }}
                >
                  <Tab icon={<ComputerIcon />} label="My Device" />
                  <Tab icon={<img src={ai_icon} className="opacity-75" />} label="Generate" />
                  {/* <Tab icon={<GoogleDriveIcon />} label="Google Drive" />
                  <Tab icon={<DropboxIcon />} label="Dropbox" />  */}
                </Tabs>

                <TabPanel value={selectedTab} index={0}>
                  <Box
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    sx={{
                      border: '2px dashed',
                      borderColor: dragActive ? 'primary.main' : 'grey.300',
                      borderRadius: 2,
                      p: 3,
                      textAlign: 'center',
                      backgroundColor: dragActive ? 'action.hover' : 'background.paper',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'action.hover'
                      }
                    }}
                  >
                    <input
                      type="file"
                      accept=".jpg, .png, .webp"
                      onChange={handleInputChange}
                      style={{ display: "none" }}
                      id="fileInput"
                    />
                    <label htmlFor="fileInput" style={{ width: '100%', cursor: 'pointer' }}>
                      <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        {selectedFile && !isGenerated ? selectedFile.name : 'Drag and drop files here'}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {selectedFile && !isGenerated ? 'Click to change file' : 'or click to browse'}
                      </Typography>
                    </label>
                  </Box>

                  {selectedFile && !isGenerated && (
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Selected File: {selectedFile.name}
                      </Typography>
                      {preview && (
                        <Box
                          component="img"
                          src={preview}
                          alt="Preview"
                          sx={{
                            width: '200px',
                            height: '100px',
                            objectFit: 'cover',
                            borderRadius: 1
                          }}
                        />
                      )}
                    </Box>
                  )}
                </TabPanel>

                <TabPanel value={selectedTab} index={1}>
                  <ImageGenerationPanel
                    generativeModels={generativeModels}
                    selectedGenerativeModel={selectedGenerativeModel}
                    setSelectedGenerativeModel={setSelectedGenerativeModel}
                    handleGenerateContent={handleGenerateContent}
                    setAIPrompt={setAIPrompt}
                    setSelectedStyle={setSelectedStyle}
                    AIPrompt={AIPrompt}
                    selectedStyle={selectedStyle}
                    aiLoading={aiLoading}
                  />
                  {selectedFile && isGenerated && (
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Selected File: {selectedFile.name}
                      </Typography>
                      {preview && (
                        <Box
                          component="img"
                          src={preview}
                          alt="Preview"
                          sx={{
                            width: '200px',
                            height: '100px',
                            objectFit: 'cover',
                            borderRadius: 1
                          }}
                        />
                      )}
                    </Box>
                  )}
                </TabPanel>
                {errors.file && (
                  <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                    {errors.file.message}
                  </Typography>
                )}
              </Box>
            </>
          )}
          {error && (
            <Typography variant="body2" color="error" sx={{ marginBottom: 2, mt: 1 }}>
              {error}
            </Typography>
          )}
          <Button type="submit" fullWidth variant="contained" color="primary" sx={{ mt: 2 }}>
            Publish
          </Button>
          {loading && <LoadingOverlay />}
        </ModalBox>
      </Modal>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={severity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {severity === "success" ? successMessage : errorMessage}
        </Alert>
      </Snackbar>
      <GeneratedImagePreview
        handleInputChange={handleInputChange}
        pendingFile={pendingFile}
        previewImage={previewImage}
        isModalOpen={isModalOpen}
        onConfirmPreview={handleCloseOrSuccessGeneratedPreview}
        onClosePreview={handleCloseOrSuccessGeneratedPreview}
      />
    </>
  );
};

export default AddMedia;
